// Shared inline editor used by both CommentComposer (create) and
// CommentCard's edit mode. Renders the textarea + footer (tag picker +
// submit/cancel buttons). Auto-grows the textarea, accounting for the
// border so single-line content isn't clipped.

import React, { useEffect, useRef } from "react";
import { Button } from "antd";
import { useTranslation } from "react-i18next";
import type { CommentTag } from "@shared/types";
import {
  FONT_FAMILY,
  RADIUS,
  SHADOW,
  SPACE,
  SURFACE,
  TEXT,
  TONE,
} from "@ds/tokens/palette";
import { Box } from "@ds/layout/Box";
import { Stack } from "@ds/layout/Stack";
import { Inline } from "@ds/layout/Inline";
import { CommentTagSelect } from "./StaticSelects";

interface CommentEditorProps {
  content: string;
  onChange: (content: string) => void;
  tag: CommentTag | null;
  onTagChange: (tag: CommentTag | null) => void;
  onSubmit: () => void;
  onCancel?: () => void;
  submitLabel: string;
  /** Hide the tag picker — used by compact popovers. Default false. */
  hideTag?: boolean;
  /** Show the footer (tag picker + buttons) even when content is empty.
   *  Used by edit mode where the user is committed to the editor. */
  alwaysShowFooter?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

export const CommentEditor: React.FC<CommentEditorProps> = ({
  content,
  onChange,
  tag,
  onTagChange,
  onSubmit,
  onCancel,
  submitLabel,
  hideTag = false,
  alwaysShowFooter = false,
  autoFocus = false,
  disabled = false,
  placeholder,
}) => {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const canSubmit = !disabled && content.trim().length > 0;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (canSubmit) onSubmit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      if (onCancel) onCancel();
    }
  };

  // Auto-grow textarea. With box-sizing: border-box, scrollHeight excludes
  // the border, so we add it back — otherwise single-line content gets
  // clipped by ~2px at the bottom.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const cs = window.getComputedStyle(el);
    const borderH = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    el.style.height = `${el.scrollHeight + borderH}px`;
  }, [content]);

  const showFooter = alwaysShowFooter || content.trim().length > 0;

  return (
    <Box grow>
      <Stack gap="sm">
        <textarea
        ref={textareaRef}
        value={content}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder ?? t("comments.placeholders.content")}
        rows={1}
        maxLength={2000}
        autoFocus={autoFocus}
        disabled={disabled}
        style={{
          display:    "block",
          width:      "100%",
          minHeight:  38,
          resize:     "none",
          overflow:   "hidden",
          fontFamily: FONT_FAMILY.sans,
          fontSize:   13.5,   // 13.5 px — off-scale: mid-point between FONT_SIZE.body (13) and FONT_SIZE.normal (14); Toolbar precedent
          color:      TEXT.primary,
          background: SURFACE.surfaceSoft,
          border:     `1px solid ${content ? TONE.blue.base : SURFACE.border}`,
          borderRadius: RADIUS.lg,
          padding:    `${SPACE.sm}px ${SPACE.md}px`,
          outline:    "none",
          boxSizing:  "border-box",
          lineHeight: 1.5,
          transition: `border-color ${120}ms ease, box-shadow ${120}ms ease`, // 120 ms off-scale per Toolbar precedent
          boxShadow:  content ? SHADOW.focusBlue : "none",
        }}
      />

      {showFooter && (
        <Inline gap="sm" align="center">
          {!hideTag && (
            <CommentTagSelect
              size="small"
              value={tag ?? undefined}
              onChange={(v) => onTagChange((v) ?? null)}
              disabled={disabled}
            />
          )}

          <Box as="span" grow />

          {onCancel && (
            <Button
              type="default"
              size="small"
              onClick={onCancel}
              disabled={disabled}
            >
              {t("common.actions.cancel")}
            </Button>
          )}

          <Button
            type="primary"
            size="small"
            onClick={onSubmit}
            disabled={!canSubmit}
          >
            {submitLabel}
          </Button>
        </Inline>
      )}
      </Stack>
    </Box>
  );
};
